import React, { useEffect, useState } from 'react';
import { Row } from '@src/components/Utils';
import { FormError } from '@src/components/Form/FormError';
import { stylesheet } from 'typestyle';
import { api } from '@src/services/api';
import logo from '../../assets/img/site/logo_alone_white.png';
import { LoginContainer } from './container/LoginContainer';
import { ForgotEmailSent } from './ForgotEmailSent';
import FormPasswordInput from './FormPasswordInput';
import { LoginTextInput } from './TextInput';
import { LoginFrameContainer } from './container/FrameContainer';
import { LoginSigninLabel } from './SigninLabel';
import { LoginSubmitButton } from './LoginSubmitButton';
import Captcha from './Captcha';
import { IS_TURNSTILE_ENABLED } from 'config';
import { verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '@src/config/firebaseAuth';
import * as appHelpers from '@src/services/helpers/appHelpers';
import { Pathname } from '@src/types/Browsing';
import { isEmailValid } from './utils';

export default function ChangePassword(): JSX.Element {
  const oobCode: string | undefined = new URLSearchParams(window.location.search).get('oobCode') ?? undefined;
  const [email, setemail] = useState<string>("");
  const [error, seterror] = useState<string | undefined>(undefined);
  const [status, setstatus] = useState<'onpage' | 'emailsent' | 'changepassword' | 'invalidCode'>(oobCode ? 'verifying' : 'onpage' as any);
  const [captchaToken, setCaptchaToken] = useState<string | undefined>(undefined);
  const [captchaReady, setCaptchaReady] = useState(!IS_TURNSTILE_ENABLED);

  useEffect(() => {
    if (!oobCode) return;
    verifyPasswordResetCode(auth, oobCode)
      .then(() => setstatus('changepassword'))
      .catch(() => {
        appHelpers.addAlert({ text: 'common.error.expired-action-code', type: 'danger' });
        appHelpers.setRoute(Pathname.login);
      });
  }, []);

  function handleCaptchaTokenChange(token: string) {
    setCaptchaToken(token);
    setCaptchaReady(true);
  }

  function handleUsername(event: { target: { value: string } }) {
    setemail(event.target.value);
  }

  function requestPassChange() {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      seterror('Missing email');
      return;
    }
    if (!isEmailValid(trimmedEmail)) {
      seterror('Invalid email');
      return;
    }
    if (IS_TURNSTILE_ENABLED && !captchaToken) {
      seterror("Invalid Captcha");
    }
    else {
      api.resetPassword(trimmedEmail, captchaToken ?? '').then(() => {
        seterror(undefined);
        setstatus('emailsent');
      }).catch((e) => {
        seterror(e.message);
      });
    }
  }

  return (
    <LoginContainer>
      <div className="logo">
        <img src={logo} />
      </div>
      <LoginFrameContainer>
        {status === 'onpage'
          ? (
            <>
              <LoginSigninLabel title={window.T('changepassword.title')} />
              <Row>
              <div id="script_cf" ></div>
              <div className="col-sm-10 offset-sm-1">
                <span style={{
                  fontSize: 12, fontWeight: 'bold', color: '#CCC', lineHeight: '15px', display: 'block',
                }}
                >
                  {window.T('changepassword.description')}
                </span>
              </div>
            </Row>
            <Row>
              <div className="col-sm-10 offset-sm-1">
                <LoginTextInput
                  label="email"
                  type="email"
                  fullWidth
                  onChange={handleUsername}
                />
              </div>
            </Row>
            {IS_TURNSTILE_ENABLED ? (
            <Row>
              <Captcha
                appearance="always"
                onTokenChange={handleCaptchaTokenChange}
              />
            </Row>
            ) : null}
            <Row className={sheet.submitRow}>
              <LoginSubmitButton
                text={window.T('submit').toUpperCase()}
                disabled={!captchaReady}
                onClick={requestPassChange}
              />
            </Row>
            {error
              ? (
                <FormError
                    error={error && window.T(`${error}`)}
                    level="error"
                  />
                )
                : null}
            </>
          ) : null }
        {status === 'emailsent'
          ? ( <ForgotEmailSent email={email} />) : null }
        {oobCode && status === 'changepassword'
          ? (
            <FormPasswordInput oobCode={oobCode} />
          ) : null }
      </LoginFrameContainer>
    </LoginContainer>
  );
}

const sheet = stylesheet({
  submitRow: {
    margin: '7px 0',
  },
});
